/**
 * Stale-chunk recovery, run as an inline `<head>` script (app/layout.tsx).
 * Asset URLs are content-hashed (no Vercel ?dpl= skew param), so a tab opened on
 * a previous deploy can request a dynamically imported chunk whose hashed URL no
 * longer exists on the current build. The script reloads once to pull fresh
 * HTML + chunks, guarded against reload loops.
 *
 * Dependency-free on purpose: the constants below are shared with app code
 * (`importWithRetry`, `autoRecovery`, `recentFailures`) and interpolated into the
 * script at build time, so the two sides can't drift apart.
 */

/** Cooldown stamp shared with `importWithRetry`, so the two reload paths can't loop each other. */
export const CHUNK_RELOAD_AT_KEY = 'kalshi-chunk-reload-at';
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/** Reload record consumed by `flushReloadRecovery` on the next page (see autoRecovery.ts). */
export const AUTO_RECOVERY_KEY = 'kalshi-auto-recovery';

/** Reloads the script refused, queued for `installInlineBlockedRecoveryBridge` (autoRecovery.ts). */
export const INLINE_BLOCKED_RECOVERIES_GLOBAL = '__kalshiBlockedRecoveries';
export const INLINE_BLOCKED_RECOVERY_EVENT = 'kalshi:auto-recovery-blocked';
const INLINE_BLOCKED_RECOVERIES_MAX = 20;

export const CHUNK_ERROR_PATTERN =
    /Loading chunk [^ ]+ failed|ChunkLoadError|Loading CSS chunk|dynamically imported module|Importing a module script failed/i;

// Kept ES5: it runs before any bundle, and must work even when no application chunk can load.
export function chunkReloadScript(sourceVersion: string): string {
    return `
(function () {
    var KEY = ${JSON.stringify(CHUNK_RELOAD_AT_KEY)};
    var BLOCKED = ${JSON.stringify(INLINE_BLOCKED_RECOVERIES_GLOBAL)};
    function isChunkError(message) {
        return ${CHUNK_ERROR_PATTERN}.test(message || '');
    }
    // A refused reload leaves the page broken, so it is queued to be reported as
    // auto_recovery_blocked once RUM is up.
    function blocked(guard) {
        try {
            var queued = window[BLOCKED] = window[BLOCKED] || [];
            if (queued.length < ${INLINE_BLOCKED_RECOVERIES_MAX}) queued.push({ guard: guard, occurred_at: Date.now() });
            window.dispatchEvent(new Event(${JSON.stringify(INLINE_BLOCKED_RECOVERY_EVENT)}));
        } catch (e) {
            console.warn('[AutoRecovery] could not queue a blocked chunk reload; it will be missing from RUM', e);
        }
    }
    function recover(trigger, error, target, filename) {
        try {
            var last = Number(sessionStorage.getItem(KEY) || 0);
            if (Date.now() - last < ${CHUNK_RELOAD_COOLDOWN_MS}) {
                blocked('recent_reload');
                return;
            }
            sessionStorage.setItem(KEY, String(Date.now()));
        } catch (e) {
            // Storage is unavailable (blocked/sandboxed), so we can't
            // record that we already reloaded. Skip recovery rather than
            // reload unguarded — a persistently failing chunk would
            // otherwise trap the user in an infinite reload loop.
            blocked('storage_unavailable');
            return;
        }
        try {
            var record = {
                reason: 'chunk_load',
                source: 'inline_chunk_reload',
                recovery_type: 'reload',
                source_version: ${JSON.stringify(sourceVersion).replace(/</g, '\\u003c')},
                occurred_at: Date.now()
            };
            // Captured by the recoveryDiagnostics.ts inline script, which runs just before this one.
            try {
                record.diagnostics = window.__kalshiRecoveryDiagnostics.capture(trigger, error, target, filename);
            } catch (e) {}
            sessionStorage.setItem(${JSON.stringify(AUTO_RECOVERY_KEY)}, JSON.stringify(record));
            if (record.diagnostics) window.__kalshiRecoveryDiagnostics.remember(record.diagnostics.recovery_id);
        } catch (e) {
            // Telemetry failure must not prevent a guarded reload.
            console.warn('[AutoRecovery] could not stamp chunk reload; this recovery will be missing from RUM', e);
        }
        window.location.reload();
    }
    window.addEventListener('error', function (event) {
        var target = event && event.target;
        if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
            var url = target.src || target.href || '';
            if (url.indexOf('/_next/static/') !== -1) recover('resource_error', null, target);
            return;
        }
        if (isChunkError(event && event.message)) recover('window_error', event.error || event.message, null, event.filename);
    }, true);
    window.addEventListener('unhandledrejection', function (event) {
        var reason = event && event.reason;
        if (isChunkError(reason && (reason.message || String(reason)))) recover('unhandledrejection', reason);
    });
})();
`;
}
